import { Table } from '@radix-ui/themes'
import { Typography } from 'antd'
import { CantileverButton } from 'components/CantileverButton'
import styled, { css } from 'styled-components'

const { Text } = Typography

// Common mixins for repeated patterns
const flexColumn = css`
  display: flex;
  flex-direction: column;
`

export const SectionContent = styled.div`
  ${flexColumn};
  gap: 0.5rem;
`

export const SensorControls = styled.div`
  ${flexColumn};
  gap: 0.5rem;
  padding: 0.25rem;
  border-radius: 0.25rem;
`

export const SmallText = styled(Text)`
  font-size: 0.7rem;
`

export const FadedSpan = styled.span`
  opacity: 0.6;
`

export const SpacedSection = styled.div`
  ${flexColumn};
  gap: 10px;
`

export const TabsRowContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  border-bottom: 2px solid ${({ theme }) => theme.forecast.border};
`

export const TabButton = styled(CantileverButton)<{
  $isSelected: boolean
}>`
  display: flex;
  align-items: center;
  flex: 1;
  gap: 0.5rem;
  border-radius: 0;
  background-color: ${({ theme, $isSelected }) =>
    $isSelected
      ? theme.forecast.defaultButton
      : theme.forecast.defaultGhostButton};

  &:not(:last-child) {
    border-right: 2px solid ${({ theme }) => theme.forecast.border};
  }
`
export const DateInputsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

export const DateInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  justify-content: space-between;

  @container (max-width: 300px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`

export const DateLabel = styled.div`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  font-weight: 500;
  flex-shrink: 0;

  @container (max-width: 300px) {
    width: 100%;
  }
`

export const ToggleAndLabelContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 1rem;
  align-items: center;
`

export const SectionLabel = styled.div`
  color: ${({ theme }) => theme.forecast.defaultText};
  font-weight: 600;
`

export const ModeToggleContainer = styled.div`
  display: flex;
  gap: 0.125rem;
  background-color: ${({ theme }) => theme.forecast.cardGroupBackground};
  border-radius: 0.25rem;
  padding: 0.25rem;
`

export const FullWidthButton = styled(CantileverButton)`
  flex: 1;
  width: 100%;
`

// ScatterPlot Table shared styles
export const ScatterPlotTableContainer = styled.div`
  width: 100%;
  max-height: 100%;
  color: ${({ theme }) => theme.forecast.defaultText};
`
export const ScatterplotTableRoot = styled(Table.Root)`
  --table-cell-min-height: 2.5rem;
  --table-cell-padding: 0.25rem;

  .rt-TableRootTable {
    --table-row-box-shadow: none;
  }
`

export const ScatterPlotEmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  background-color: ${({ theme }) => theme.forecast.cardGroupBackground};
  border-radius: 0.375rem;
`

export const ScatterPlotEmptyStateTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.defaultText};
`

export const ScatterPlotEmptyStateDescription = styled.p`
  font-size: 0.875rem;
  line-height: 1;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

export const ScatterPlotColorSwatch = styled.div<{ color: string }>`
  width: 0.625rem;
  height: 0.625rem;
  background-color: ${({ color }) => color};
  border-radius: 100%;
`

export const ScatterPlotNameCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`

export const ScatterPlotSensorId = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.defaultText};
`

export const ScatterPlotDescriptionText = styled.span`
  font-size: 0.625rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  opacity: 0.8;
`

export const ScatterPlotValueInfoCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`
export const ScatterPlotValueWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-end;
  gap: 0.5rem;
`

export const ScatterPlotCurrentValue = styled.div<{
  $size?: 'small'
}>`
  font-size: ${({ $size }) => ($size === 'small' ? '.5rem' : '.8rem')};
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.activeText};
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum';
  display: flex;
  gap: 0.25rem;
  justify-content: flex-start;
  text-align: right;

  > span:first-child {
    text-align: right;
    display: inline-block;
    min-width: 3.5rem;
  }
`

export const ScatterPlotStyledRow = styled.div`
  min-height: 2rem;
`
